import React, {useCallback, useEffect, useMemo, useState} from "react"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import {Box, Grid, Paper, Typography} from "@mui/material"
import {ReactComponent as PlusSquareIcon} from "src/v2/assets/icons/plus-square-icon.svg"
import {useNavigate} from "react-router-dom"
import {MOTOR_CADASTRO_MANUAL_PAGE} from "src/utils/routes"
import {dispatchSnackbar} from "../../../components/SnackBar/snackbar-bus"
import MotorService from "../../../../services/motor-service"
import {createColumnsCadastroMotor} from "./cadastro-motor-const"
import ButtonWithIcon from "../../../components/Button/Button"
import DataGridTable from "../../../components/Table/data-grid-table"
import DuplicarModal from "../components/DuplicarModal/duplicar-modal"
import OcrPromptComponent from "../components/OCR/ocr-prompt-component"
import OcrTableComponent from "../components/OCR/ocr-table-component"
import CetipVersaoFilterComponent from "../components/CetipVersaoFilter/cetip-versao-filter-component"

const TITLE_MOTOR_EVENTOS_LIST_PAGE = "Cadastro de Operações"
const OCR_SOURCE = "caracteristicas"

const MotorCadastroListPage = () => {
    const [cetip, setCetip] = useState("")
    const [versaoDocumento, setVersaoDocumento] = useState("")
    const [loadingTable, setLoadingTable] = useState(false)
    const [loadingPage, setLoadingPage] = useState(false)
    const [rows, setRows] = useState([])

    const pagination = {page: 0}
    const navigate = useNavigate()
    const service = useMemo(() => new MotorService(), [])
    const [modalDuplicarOpen, setModalDuplicarOpen] = useState(false)
    const [linhaSelecionada, setLinhaSelecionada] = useState(null)

    useEffect(() => {
        loadCadastrosEmAndamento()
    }, [])

    const handleDuplicarCadastro = useCallback((row) => {
        setLinhaSelecionada(row)
        setModalDuplicarOpen(true)
    }, [])

    const loadCadastros = (cetip, versao) => {
        setLoadingTable(true)
        service.loadCadastros(cetip, versao)
            .then((response) => {
                const {body} = response
                const list = body.map((obj, index) => ({...obj, id: index}))
                setRows(list)
            })
            .catch(() => {
                dispatchSnackbar("Erro ao carregar os registros!", "error")
            })
            .finally(() => {
                setLoadingTable(false)
            })
    }

    const loadCadastrosEmAndamento = async () => {
        setLoadingTable(true)
        try {
            const response = await service.loadCadastros(null, null)

            const list = (response.body ?? [])
                .filter(item => item.Status === "Em andamento")
                .sort((a, b) => {
                    if (a.CETIP !== b.CETIP) {
                        return a.CETIP.localeCompare(b.CETIP)
                    }
                    return Number(a.VersaoDocumento) - Number(b.VersaoDocumento)
                })
                .map((obj, index) => ({...obj, id: index}))

            setRows(list)
        } catch {
            dispatchSnackbar("Erro ao carregar os registros!", "error")
        } finally {
            setLoadingTable(false)
        }
    }

    const iniciaCadastroManual = () => {
        navigate(MOTOR_CADASTRO_MANUAL_PAGE, {
            state: {
                cetip: cetip,
                versaoDocumento: versaoDocumento
            }
        })
    }

    const handleVerCadastro = useCallback(
        (row) => {
            const cetip = row?.CETIP
            const versao = row?.VersaoDocumento
            const id = row?.ID
            if (!cetip) {
                dispatchSnackbar("CETIP inválido para visualização", "warning")
                return
            }
            navigate(MOTOR_CADASTRO_MANUAL_PAGE, {
                state: {cetip, versaoDocumento: versao, id},
            })
        },
        [navigate]
    )

    const columns = useMemo(
        () => createColumnsCadastroMotor(handleVerCadastro, handleDuplicarCadastro),
        [handleVerCadastro, handleDuplicarCadastro]
    )

    const handleConfirmarDuplicacao = (dadosDoModal) => {
        const row = dadosDoModal.original

        let dadosAntigosTratados = {}

        if (row.ID) {
            dadosAntigosTratados = {id: row.ID}
        } else {
            dadosAntigosTratados = {
                cetip: row.CETIP,
                versaoDocumento: row.VersaoDocumento
            }
        }

        const payload = {
            origem: 'duplicacao',
            dadosAntigos: dadosAntigosTratados,
            novosDados: {
                cetip: dadosDoModal.novoCetip,
                versao: dadosDoModal.novaVersao
            }
        }
        navigate(MOTOR_CADASTRO_MANUAL_PAGE, {state: payload})
        setModalDuplicarOpen(false)
    }

    return (
        <SideBarLayout
            title={TITLE_MOTOR_EVENTOS_LIST_PAGE}
            header
            loading={loadingPage}
            headerTitle={TITLE_MOTOR_EVENTOS_LIST_PAGE}
            headerImage={"/background/cadastro-background-header.jpg"}
        >
            <Box sx={{mt: -2}}>
                <CetipVersaoFilterComponent
                    setCetip={setCetip}
                    setVersao={setVersaoDocumento}
                    returnCetipVersao={(cetipReturned, versaoReturned) => {
                        setRows([])
                        loadCadastros(cetipReturned, versaoReturned)
                        setCetip(cetipReturned)
                        setVersaoDocumento(versaoReturned)
                    }}
                    clearFilter={() => {
                        setCetip("")
                        setVersaoDocumento("")
                        loadCadastrosEmAndamento()
                    }}
                />
                <br/>

                {/* Container para inicio do cadastro */}
                <Grid container spacing={3} direction="row">
                    {/* Item do leitor de OCR */}
                    <Grid item xs={12} md={8}>
                        <OcrPromptComponent
                            source={OCR_SOURCE}
                            callback={() => {}}
                            cetip={cetip}
                            versaoDocumento={versaoDocumento}
                            loading={setLoadingPage}/>
                    </Grid>

                    {/* Item do cadastro manual */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{p: 3, height: '100%', borderRadius: 0}}>
                            <Grid container direction="column" alignItems="center" justifyContent="center" spacing={1}>
                                <Grid item sx={{mb: 12}}>
                                    <Typography variant="body1" component="span" color="common.white">
                                        CADASTRO MANUAL
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <ButtonWithIcon
                                        icon={<PlusSquareIcon sx={{fontSize: 18}}/>}
                                        onClick={iniciaCadastroManual}>
                                        INICIAR CADASTRO MANUAL
                                    </ButtonWithIcon>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
                <br/>

                {/* Lista de cadastros OCR */}
                <OcrTableComponent source={OCR_SOURCE} paginaCadastroOCR={MOTOR_CADASTRO_MANUAL_PAGE}/>
                <br/>

                {/* Lista de cadastros */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{p: 3, borderRadius: 0}}>
                        <Box
                            sx={{
                                flex: 1,
                                overflowX: "auto",
                                width: "100%",
                                display: "block",
                            }}
                        >
                            <DataGridTable
                                columns={columns}
                                rows={rows}
                                pagination={pagination}
                                disableDeleteColumn
                                loading={loadingTable}
                                resetKey={`cadastro-motor`}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Box>
            <DuplicarModal
                open={modalDuplicarOpen}
                onClose={() => setModalDuplicarOpen(false)}
                rowOriginal={linhaSelecionada}
                onConfirm={handleConfirmarDuplicacao}
            />
        </SideBarLayout>
    )
}

export default MotorCadastroListPage
